// Whether probing the (HttpOnly) auth cookies is worth a request, plus the
// pre-hydration hint that lets the header render without waiting for React.

// Set once a session is confirmed (login or a successful probe), cleared on
// logout or a failed probe. The auth cookies themselves are HttpOnly, so this
// is the only first-hand evidence the client has that a probe can succeed.
const SESSION_MARKER = 'outpoll_has_session';
// Set after the first probe in this browser, whatever its outcome. It lets a
// session established before the marker existed (or by a flow that never
// wrote the obfuscated auth blob) be picked up exactly once instead of never.
const PROBED_MARKER = 'outpoll_session_probed';
// Non-secret UI cache written by the auth flows; also counts as session evidence.
const OBF_AUTH_KEY = 'outpoll_auth_obf';
// Every key that makes a probe look worth trying — a dead session must clear
// all of them, or hasSessionEvidence() stays true and the doomed probe (two
// 401s) re-runs on every load.
const EVIDENCE_KEYS = [SESSION_MARKER, OBF_AUTH_KEY];

export function hasSessionEvidence(): boolean {
  try {
    if (EVIDENCE_KEYS.some((key) => localStorage.getItem(key))) return true;
    if (document.cookie.includes('XSRF-TOKEN=')) return true;
    return !localStorage.getItem(PROBED_MARKER);
  } catch {
    // No storage access (privacy mode, disabled cookies): behave as before.
    return true;
  }
}

export function rememberSession(active: boolean): void {
  try {
    localStorage.setItem(PROBED_MARKER, '1');
    if (active) localStorage.setItem(SESSION_MARKER, '1');
    else EVIDENCE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {}
}

// The header can only be right in the first paint if this decision happens
// while the browser parses the HTML, before React exists. Written as a real
// function and stringified for the inline tag (see SessionEvidenceScript):
// it must stay self-contained, so the keys come in as an argument instead of
// closing over the constants above.
function markNoSession(keys: { session: string; obf: string; probed: string }) {
  try {
    const evidence =
      localStorage.getItem(keys.session) ||
      localStorage.getItem(keys.obf) ||
      document.cookie.includes('XSRF-TOKEN=') ||
      !localStorage.getItem(keys.probed);
    if (!evidence) document.documentElement.setAttribute('data-no-session', '');
  } catch {}
}

export const SESSION_EVIDENCE_SCRIPT = `(${markNoSession})(${JSON.stringify({
  session: SESSION_MARKER,
  obf: OBF_AUTH_KEY,
  probed: PROBED_MARKER,
})})`;
